import { useState, useCallback, useEffect } from 'react';
import { BOTTOM_NAVI } from '../../../data/define';
import toast from 'react-hot-toast';
import Url from '../../../data/url';
import {
  useMyInfoQuery,
  useEditMyProfileImageMutation,
  useEditMyInfoMutation,
} from '../../../queries';

import FullHeightContainer from '../../../component/common/FullHeightContainer';
import { useNavigate } from 'react-router-dom';
import TopHeaderWithPrev from '../../../component/common/TopHeaderWithPrev';
import BottomNavigation from '../../../component/common/BottomNavigation';
import ParentProfileEdit from '../../../component/my/ParentProfileEdit';
import LoadingBackdrop from '../../../component/common/LoadingBackdrop';
import { useTranslation } from '../../../hook/useTranslation';

interface ProfileFile {
  file: File | null;
  url: string | null;
  name: string | null;
}

export default function ParentProfileEditPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [profileFile, setProfileFile] = useState<ProfileFile | null>(null);
  const [nickName, setNickName] = useState('');

  const { data: userProfile } = useMyInfoQuery();
  const { mutateAsync: editMyProfileImage } = useEditMyProfileImageMutation(
    userProfile?.id as number
  );
  const { mutateAsync: editMyInfo } = useEditMyInfoMutation();

  useEffect(() => {
    if (userProfile) {
      setNickName(userProfile.nickName || '');
      setProfileFile({
        file: null,
        url: userProfile.imageUrl || '',
        name: null,
      });
    }
  }, [userProfile]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!nickName) {
        toast.error(t('my.profile.toast_nickname_required'));
        return;
      }
      setLoading(true);
      let profileRes;
      if (profileFile?.file && userProfile?.id) {
        const fd = new FormData();
        fd.append('file', profileFile?.file);
        profileRes = await editMyProfileImage(fd);
      }
      const infoRes = await editMyInfo({
        nickName,
      });
      if (
        infoRes.status === 200 ||
        (profileRes && profileRes.status >= 200 && profileRes.status < 300)
      ) {
        navigate(-1);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('my.profile.toast_edit_fail'));
    } finally {
      setLoading(false);
    }
  }, [profileFile, nickName, userProfile, navigate, editMyProfileImage, editMyInfo, t]);

  return (
    <FullHeightContainer>
      {loading && <LoadingBackdrop open={loading} />}
      <TopHeaderWithPrev title={t('my.profile.parent_edit_title')} onClickPrev={() => navigate(Url.My)} />
      <button
        type="button"
        className="absolute top-3 right-4 font-[400] text-[16px] text-[#1A1A1A]"
        onClick={handleSubmit}
      >
        {t('common.done')}
      </button>
      <div className="pageInner">
        <ParentProfileEdit
          userProfile={userProfile}
          profileFile={profileFile}
          setProfileFile={setProfileFile}
          nickName={nickName}
          setNickName={setNickName}
        />
      </div>
      <BottomNavigation focusTab={BOTTOM_NAVI.MY} />
    </FullHeightContainer>
  );
}
